import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldCheck, CheckCircle2, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Appearance configured in Checkout Studio.
const appearance = {
  theme: "stripe",
  labels: "auto",
  inputs: "spaced",
  variables: {
    borderRadius: "4px",
    colorBackground: "#ffffff",
    colorDanger: "#df1b41",
    colorPrimary: "#fbab50",
    colorSuccess: "#00c853",
    colorText: "#30313d",
    fontFamily: "default",
    fontSizeBase: "16px",
    spacingUnit: "4px",
  },
};

const waitForStripe = (tries = 50) =>
  new Promise((resolve, reject) => {
    let n = 0;
    const tick = () => {
      if (window.Stripe) return resolve(window.Stripe);
      if (n++ > tries) return reject(new Error("Stripe.js failed to load"));
      setTimeout(tick, 100);
    };
    tick();
  });

const Shell = ({ children }) => (
  <div className="min-h-screen bg-ink px-5 py-10 text-white" data-testid="checkout-page">
    <div className="mx-auto max-w-lg">
      <Link to="/" className="mb-6 inline-flex items-center gap-2 text-sm text-white/60 transition-colors hover:text-gold" data-testid="checkout-back-home">
        <ArrowLeft size={16} /> Upper Class Chauffeurs
      </Link>
      {children}
    </div>
  </div>
);

export default function CheckoutPage() {
  const { ref } = useParams();
  const started = useRef(false);
  const [status, setStatus] = useState("loading"); // loading | ready | succeeded | error
  const [error, setError] = useState("");
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        const Stripe = await waitForStripe();
        const { data: cfg } = await axios.get(`${API}/stripe/config`);
        if (!cfg.publishable_key) {
          setError("Online payments are not configured yet. Please contact us to complete your booking.");
          setStatus("error");
          return;
        }
        const stripe = Stripe(cfg.publishable_key, { betas: ["custom_checkout_payment_form_1"] });
        const { data } = await axios.post(`${API}/create-checkout-session`, { booking_ref: ref });
        setAmount(data.amount);
        const sessionId = data.session_id;

        const checkout = stripe.initCheckoutFormSdk({ clientSecret: data.client_secret, appearance });
        const form = checkout.createForm({ layout: "expanded" });
        form.mount("#checkout-form");

        const loaded = await checkout.loadActions();
        if (loaded.type === "success") {
          form.on("confirm", async (event) => {
            setError("");
            try {
              const result = await loaded.actions.confirm({ formConfirmEvent: event });
              if (result && result.type === "error") {
                setError(result.error?.message || "Payment could not be completed.");
              } else {
                setStatus("succeeded");
                try { await axios.post(`${API}/checkout/finalize`, { session_id: sessionId }); } catch (_) {}
              }
            } catch (err) {
              setError(err?.message || "Payment could not be completed.");
            }
          });
        }
        setStatus("ready");
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || "Something went wrong.");
        setStatus("error");
      }
    })();
  }, [ref]);

  if (status === "succeeded") {
    return (
      <Shell>
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center" data-testid="checkout-success">
          <CheckCircle2 size={44} className="mx-auto text-emerald-400" />
          <h1 className="mt-4 font-heading text-2xl text-white">Payment received</h1>
          <p className="mt-2 text-sm text-white/60">Thank you. Your booking {ref} is confirmed — we'll be in touch shortly.</p>
          <Link to="/" className="mt-6 inline-block rounded-md bg-gold px-5 py-2 text-sm font-medium uppercase tracking-wider text-ink" data-testid="checkout-success-home">
            Back to home
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-5">
        <h1 className="font-heading text-2xl font-light text-white sm:text-3xl" data-testid="checkout-title">
          Complete your <span className="italic text-gold">payment</span>
        </h1>
        <p className="mt-1 text-sm text-white/55" data-testid="checkout-ref">
          Booking {ref}
          {amount != null ? <> · <span className="text-gold">£{Number(amount).toFixed(2)}</span></> : null}
        </p>
      </div>

      {status === "error" && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300" data-testid="checkout-error">
          {error}
        </div>
      )}

      {status === "loading" && (
        <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-6 text-sm text-white/60" data-testid="checkout-loading">
          <Loader2 size={18} className="animate-spin text-gold" /> Preparing secure checkout…
        </div>
      )}

      {error && status === "ready" && (
        <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300" data-testid="checkout-inline-error">
          {error}
        </div>
      )}

      {/* Stripe renders the embedded payment form inside this container */}
      <div className={status === "ready" ? "rounded-2xl bg-white p-4" : "hidden"}>
        <div id="checkout-form" data-testid="checkout-form" />
      </div>

      {status === "ready" && (
        <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-white/40" data-testid="checkout-secure-note">
          <ShieldCheck size={13} className="text-gold/70" /> Payments are securely processed by Stripe.
        </p>
      )}
    </Shell>
  );
}

export function CheckoutReturn() {
  const { ref } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const done = useRef(false);
  useEffect(() => {
    if (done.current || !sessionId) return;
    done.current = true;
    axios.post(`${API}/checkout/finalize`, { session_id: sessionId }).catch(() => {});
  }, [sessionId]);
  return (
    <Shell>
      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center" data-testid="checkout-return">
        <CheckCircle2 size={44} className="mx-auto text-emerald-400" />
        <h1 className="mt-4 font-heading text-2xl text-white">Thank you</h1>
        <p className="mt-2 text-sm text-white/60">
          Your payment for booking {ref} is being confirmed. You'll receive a confirmation shortly.
        </p>
        {sessionId ? <p className="mt-3 break-all text-[0.65rem] text-white/30">Ref: {sessionId}</p> : null}
        <Link to="/" className="mt-6 inline-block rounded-md bg-gold px-5 py-2 text-sm font-medium uppercase tracking-wider text-ink" data-testid="checkout-return-home">
          Back to home
        </Link>
      </div>
    </Shell>
  );
}
